import { useState, useEffect } from 'react';
import { safeStorage } from '@/lib/logger';

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consented = safeStorage.get('cookie-consent', null);
    if (!consented) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs: typeof preferences) => {
    safeStorage.set('cookie-consent', prefs);
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const all = { essential: true, analytics: true, marketing: true };
    saveConsent(all);
  };

  const handleAcceptSelected = () => {
    saveConsent(preferences);
  };

  const handleDecline = () => {
    saveConsent({ essential: true, analytics: false, marketing: false });
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[150] px-3 pb-2" style={{ animation: 'slideUp 0.3s ease-out' }} role="dialog" aria-modal="true" aria-label="Cookie consent">
      <div
        className="mx-auto rounded-xl p-3"
        style={{ maxWidth: showDetails ? 560 : 820, background: 'rgba(15,25,35,0.97)', border: '1px solid rgba(212,168,67,0.12)', backdropFilter: 'blur(12px)' }}
      >
        {!showDetails ? (
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
            <p className="font-body text-center sm:text-left" style={{ fontSize: 11, lineHeight: 1.5, color: 'rgba(245,245,243,0.7)' }}>
              We use cookies to improve your experience.{' '}
              <a href="/privacy" className="underline transition-colors hover:text-[#F7F8F4]" style={{ color: '#7CFF6B' }}>Privacy</a>{' '}
              · <a href="/legal-compliance" className="underline transition-colors hover:text-[#F7F8F4]" style={{ color: '#7CFF6B' }}>Compliance</a>
            </p>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={handleDecline} className="font-body rounded-full px-3 py-1.5 text-[10px] font-medium transition-all"
                style={{ border: '1px solid rgba(255,255,255,0.2)', color: '#A8B8AE', background: 'transparent' }}>
                Reject
              </button>
              <button onClick={handleAcceptAll} className="font-body rounded-full px-4 py-1.5 text-[10px] font-medium transition-all hover:opacity-90" style={{ background: '#7CFF6B', color: '#0F1923' }}>
                Accept
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="font-body text-sm font-medium" style={{ color: '#F7F8F4' }}>Cookie Preferences</h3>
            <p className="text-xs" style={{ color: '#A8B8AE' }}>
              Under state privacy laws including CCPA/CPRA, VCDPA, CPA, CTDPA, and TDPSA, you have the right to opt out of certain data processing. Select your preferences below:
            </p>

            <div className="space-y-2">
              <CookieToggle label="Essential" desc="Required for the platform to function (login, security, preferences). Cannot be disabled." locked on />
              <CookieToggle label="Analytics" desc="Helps us understand how you use the platform so we can improve it. Uses Google Analytics 4 with IP anonymization." on={preferences.analytics}
                onChange={(v) => setPreferences(p => ({ ...p, analytics: v }))} />
              <CookieToggle label="Marketing" desc="Used to show you relevant product updates and features. We do not sell your data to third parties for advertising." on={preferences.marketing}
                onChange={(v) => setPreferences(p => ({ ...p, marketing: v }))} />
            </div>

            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setShowDetails(false)} className="text-xs transition-colors hover:opacity-80" style={{ color: '#A8B8AE' }}>
                ← Back
              </button>
              <div className="flex items-center gap-2">
                <button onClick={handleDecline} className="font-body rounded-full px-4 py-2 text-xs font-medium transition-all"
                  style={{ border: '1px solid rgba(255,255,255,0.2)', color: '#A8B8AE', background: 'transparent' }}>
                  Reject Non-Essential
                </button>
                <button onClick={handleAcceptSelected} className="font-body rounded-full px-5 py-2 text-xs font-medium transition-all hover:opacity-90" style={{ background: '#7CFF6B', color: '#0F1923' }}>
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function CookieToggle({ label, desc, locked, on, onChange }: {
  label: string; desc: string; locked?: boolean; on: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(247,248,244,0.08)' }}>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium" style={{ color: '#F7F8F4' }}>{label}</span>
          {locked && <span className="rounded px-1.5 py-0.5 text-[9px] font-medium" style={{ background: 'rgba(247,248,244,0.12)', color: '#A8B8AE' }}>Required</span>}
        </div>
        <p className="mt-0.5 text-[11px]" style={{ color: '#a9b4c0' }}>{desc}</p>
      </div>
      <button
        onClick={() => !locked && onChange?.(!on)}
        disabled={locked}
        className="relative h-5 w-9 shrink-0 rounded-full transition-colors mt-0.5"
        style={{ background: on ? '#7CFF6B' : 'rgba(247,248,244,0.12)', opacity: locked ? 0.5 : 1, cursor: locked ? 'not-allowed' : 'pointer' }}
        role="switch"
        aria-checked={on}
        aria-label={`${label} cookies ${locked ? 'required' : on ? 'enabled' : 'disabled'}`}
      >
        <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform" style={{ transform: on ? 'translateX(18px)' : 'translateX(2px)' }} aria-hidden="true" />
      </button>
    </div>
  );
}
